import React, { useMemo } from 'react';
import { TableConfig } from '../../../../../v2BaseComponents/RichTable/types';
import { APITypes } from '../../../../../../primary-record-types';
import { getIntl } from '../../../../../../utils/intl';
import { useRichTableState } from '../../../../../v2BaseComponents/RichTable/useRichTableState';
import RichTable from '../../../../../v2BaseComponents/RichTable';
import { useMaybeRenderedSideDrawerToggle } from '../../../../../v2BaseComponents/SideDrawer/MaybeRenderedSideDrawer';
import { useOrganizationTabNavigation } from '../../../../../../utils/appNavigationNames';
import { useIsOrganizationAdmin } from '../../../../../../hooks/api/useOrganization';
import _truncate from 'lodash/truncate';
import { usePromptButton } from '../../../../../shared/PromptButton';
import { ContentStack } from '../../../../../v2BaseComponents/Stack';
import InfoIcon from '../../../../../v2BaseComponents/InfoIcon';
import { PromptActions } from '../../../../../v2BaseComponents/Prompt';
import getName from '../../../../../../utils/getName';
import { organizationRoles } from '../../../../../../utils/sharedMessages';
import OrganizationUserDetailsSideDrawerContent from './OrganizationUserDetailsSideDrawerCotnent';
import useOrganizationUsers from '../../../../../../hooks/api/useOrganizationUsers';
import EditOrganizationUserSideDrawerContent from './EditOrganizationUserSideDrawerContent';
import useOrganizationUserInvitations from '../../../../../../hooks/api/useOrganizationUserInvitations';
import InvitationDetailsSideDrawerContent from './InvitationDetailsSideDrawerContent';
import { isPresent } from 'ts-is-present';
import AddOrganizationInvitationSideDrawerContent from './AddOrganizationInvitationSideDrawerContent';

const intl = getIntl('resources-root-screen-organization-organizationUser-tab-table', {
    OrganizationUserDetails: 'Details',
    invitationDetails: 'Details',
    name: 'Name',
    editOrganizationUser: 'Edit Organization User',
    isBillingAdmin: 'Is Billing Admin?',
    addInvitation: 'Add User',
    edit: 'Edit',
    yes: 'Yes',
    no: 'No',
    role: 'Role',
    deleteUser: 'Deactivate User',
    delete: 'Delete',
    deleteOrganizationUserHeader: 'Deactivate User?',
    deleteOrganizationUserContent:
        'Are you sure you want to deactivate the user "{name}"?  This action cannot be undone.',
    deleteOrganizationUserConfirm: 'Deactivate User',
    cancel: 'Cancel',
    deleteOrganizationUsersHeader: 'Deactivate Users?',
    deleteOrganizationUsersContent: 'Are you sure you want to deactivate {count} Users?  This action cannot be undone.',
    deleteOrganizationUsersConfirm: 'Deactivate Users',
    status: 'Status',
    invitePending: 'Invite Pending',
    active: 'Active',
    deleteInvitationHeader: 'Delete Invitation?',
    deleteInvitationContent:
        'Are you sure you want to delete the invitation to "{name}"?  This action cannot be undone.',
    deleteInvitationConfirm: 'Delete Invitation',
    deleteInvitationsHeader: 'Delete Invitations?',
    deleteInvitationsContent: 'Are you sure you want to delete {count} invitations?  This action cannot be undone.',
    deleteInvitationsConfirm: 'Delete Invitations',
    resendInvitationEmail: 'Resend Invitation Email',
});

type TableItem =
    | APITypes.organizationService.OrganizationUserDetails
    | APITypes.organizationService.OrganizationUserInvitationDetails;

const isOrganizationUser = (item: TableItem): item is APITypes.organizationService.OrganizationUserDetails =>
    'organizationUserId' in item;

export type OrganizationUsersTableOwnProps = {};

export type OrganizationUsersTableProps = OrganizationUsersTableOwnProps;

const OrganizationUsersTable: React.FC<OrganizationUsersTableProps> = () => {
    const organizationTabNavigator = useOrganizationTabNavigation();
    const [tableState, setTableState] = useRichTableState();
    const { deleteUser, isDeletingUser, deleteUsers, isDeletingUsers, users, isFetchingUsers } = useOrganizationUsers(
        {},
    );
    const {
        invitations,
        isFetchingInvitations,
        deleteInvitation,
        deleteInvitations,
        isDeletingInvitation,
        isDeletingInvitations,
        resendInvitationEmail,
        isResendingInvitationEmail,
    } = useOrganizationUserInvitations({});

    const getNameDisplay = (item: TableItem) =>
        isOrganizationUser(item)
            ? getName(item.user)
            : getName({ firstName: item.inviteeFirstName, lastName: item.inviteeLastName });
    const getRole = (item: TableItem) => organizationRoles(isOrganizationUser(item) ? item.role : item.inviteeRole);
    const getIsBillingAdmin = (item: TableItem) => {
        const isBillingAdmin = isOrganizationUser(item) ? item.isBillingAdmin : false;
        return isBillingAdmin ? intl('yes') : intl('no');
    };
    const getStatus = (item: TableItem) => (isOrganizationUser(item) ? intl('active') : intl('invitePending'));
    const addInvitationSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'addOrganizationUserInvitation',
        title: intl('addInvitation'),
        renderContent: (onClose) => <AddOrganizationInvitationSideDrawerContent onClose={onClose} />,
        mobileOnFocus: () => organizationTabNavigator.navigate('add-organization-invitation'),
    });
    const organizationUserDetailsSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'OrganizationUserDetails',
        title: intl('OrganizationUserDetails'),
        renderContent: () => (
            <OrganizationUserDetailsSideDrawerContent organizationUserId={tableState.selectedRowKeys[0]} />
        ),
        mobileOnFocus: () =>
            organizationTabNavigator.navigate('user', {
                organizationUserId: tableState.selectedRowKeys[0],
            }),
    });
    const editOrganizationUserSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'EditOrganizationUser',
        title: intl('editOrganizationUser'),
        renderContent: (onClose) => (
            <EditOrganizationUserSideDrawerContent
                onClose={onClose}
                organizationUserId={tableState.selectedRowKeys[0]}
            />
        ),
        mobileOnFocus: () =>
            organizationTabNavigator.navigate('edit-user', {
                organizationUserId: tableState.selectedRowKeys[0],
            }),
    });
    const invitationDetailsSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'invitationDetails',
        title: intl('invitationDetails'),
        renderContent: () => <InvitationDetailsSideDrawerContent InvitationId={tableState.selectedRowKeys[0]} />,
        mobileOnFocus: () =>
            organizationTabNavigator.navigate('organization-invitation', {
                invitationId: tableState.selectedRowKeys[0],
            }),
    });
    const isOrganizationAdmin = useIsOrganizationAdmin();
    const selectedOrganizationUsers = tableState.selectedRowKeys
        .map((key) => users.find((organizationUser) => organizationUser.organizationUserId === key))
        .filter(isPresent);
    const selectedInvitations = tableState.selectedRowKeys
        .map((key) => invitations.find((invitation) => invitation.organizationUserInvitationId === key))
        .filter(isPresent);
    const isSingleRowSelected = tableState.selectedRowKeys.length === 1;
    const isSingleUserSelected = selectedOrganizationUsers.length === 1 && isSingleRowSelected;
    const isSingleInvitationSelected = selectedInvitations.length === 1 && isSingleRowSelected;
    const singleSelectedUser = isSingleUserSelected ? selectedOrganizationUsers[0] : null;
    const singleSelectedInvitation = isSingleInvitationSelected ? selectedInvitations[0] : null;

    const deleteUserButtonModal = usePromptButton({
        renderPromptContents: (onClose) =>
            selectedOrganizationUsers[0] ? (
                <ContentStack>
                    <InfoIcon
                        alertIconProps={{
                            name: 'AlertTriangle',
                            variant: 'error',
                        }}
                        title={
                            tableState.selectedRowKeys.length === 1
                                ? intl('deleteOrganizationUserHeader')
                                : intl('deleteOrganizationUsersHeader')
                        }
                        subText={
                            tableState.selectedRowKeys.length === 1
                                ? intl('deleteOrganizationUserContent', {
                                      name: getName(selectedOrganizationUsers[0].user),
                                  })
                                : intl('deleteOrganizationUsersContent', { count: tableState.selectedRowKeys.length })
                        }
                    />
                    <PromptActions
                        actions={[
                            {
                                title:
                                    tableState.selectedRowKeys.length === 1
                                        ? intl('deleteOrganizationUserConfirm')
                                        : intl('deleteOrganizationUsersConfirm'),
                                onPress: () => {
                                    if (tableState.selectedRowKeys.length === 1) {
                                        deleteUser(tableState.selectedRowKeys[0]);
                                    } else {
                                        deleteUsers(tableState.selectedRowKeys);
                                    }
                                    onClose();
                                },
                                variant: 'alert',
                                icon: 'Trash2',
                                testID: 'delete-user-confirm',
                            },
                            {
                                title: intl('cancel'),
                                onPress: onClose,
                                variant: 'secondary',
                                icon: 'X',
                                testID: 'delete-user-cancel',
                            },
                        ]}
                    />
                </ContentStack>
            ) : null,
        promptTitle:
            tableState.selectedRowKeys.length === 1
                ? intl('deleteOrganizationUserHeader')
                : intl('deleteOrganizationUsersHeader'),
    });

    const deleteInvitationButtonModal = usePromptButton({
        renderPromptContents: (onClose) => (
            <ContentStack>
                <InfoIcon
                    alertIconProps={{
                        name: 'AlertTriangle',
                        variant: 'error',
                    }}
                    title={
                        tableState.selectedRowKeys.length === 1
                            ? intl('deleteInvitationHeader')
                            : intl('deleteInvitationsHeader')
                    }
                    subText={
                        tableState.selectedRowKeys.length === 1
                            ? intl('deleteInvitationContent', {
                                  name: getName({
                                      firstName: selectedInvitations[0]?.inviteeFirstName || '',
                                      lastName: selectedInvitations[0]?.inviteeLastName || '',
                                  }),
                              })
                            : intl('deleteInvitationsContent', { count: tableState.selectedRowKeys.length })
                    }
                />
                <PromptActions
                    actions={[
                        {
                            title:
                                tableState.selectedRowKeys.length === 1
                                    ? intl('deleteInvitationConfirm')
                                    : intl('deleteInvitationsConfirm'),
                            onPress: () => {
                                if (tableState.selectedRowKeys.length === 1) {
                                    deleteInvitation(tableState.selectedRowKeys[0]);
                                } else {
                                    deleteInvitations(tableState.selectedRowKeys);
                                }
                                onClose();
                            },
                            variant: 'alert',
                            icon: 'Trash2',
                            testID: 'delete-invitation-confirm',
                        },
                        {
                            title: intl('cancel'),
                            onPress: onClose,
                            variant: 'secondary',
                            icon: 'X',
                            testID: 'delete-invitation-cancel',
                        },
                    ]}
                />
            </ContentStack>
        ),
        promptTitle:
            tableState.selectedRowKeys.length === 1 ? intl('deleteInvitationHeader') : intl('deleteInvitationsHeader'),
    });

    const OrganizationUsersTableConfig: TableConfig<TableItem> = {
        columns: [
            {
                label: intl('name'),
                columnKey: 'name',
                getSortValue: getNameDisplay,
                getContentKey: getNameDisplay,
                getSearchValue: getNameDisplay,
                render: getNameDisplay,
            },
            {
                label: intl('status'),
                columnKey: 'status',
                getSortValue: getStatus,
                getContentKey: getStatus,
                getSearchValue: getStatus,
                render: getStatus,
                getDiscreetValue: getStatus,
            },
            {
                label: intl('role'),
                columnKey: 'role',
                getSortValue: getRole,
                getContentKey: (item) => getRole(item) || '',
                getSearchValue: getRole,
                render: getRole,
                getDiscreetValue: getRole,
            },
            {
                label: intl('isBillingAdmin'),
                columnKey: 'isBillingAdmin',
                getSortValue: getIsBillingAdmin,
                getContentKey: getIsBillingAdmin,
                getSearchValue: getIsBillingAdmin,
                getDiscreetValue: getIsBillingAdmin,
                render: getIsBillingAdmin,
            },
        ],
        getRowKey: (item) => (isOrganizationUser(item) ? item.organizationUserId : item.organizationUserInvitationId),
    };

    const data = useMemo(() => {
        return [...users, ...invitations].sort((a, b) => {
            const aName = getNameDisplay(a);
            const bName = getNameDisplay(b);
            return aName.localeCompare(bName);
        });
    }, [users, invitations]);

    return (
        <>
            <RichTable
                config={OrganizationUsersTableConfig}
                state={tableState}
                onStateChange={setTableState}
                data={data}
                minWidth={'100%'}
                allowRowSelection
                style={{ height: '100%' }}
                isFetching={isFetchingUsers || isFetchingInvitations}
                actions={[
                    {
                        title: intl('OrganizationUserDetails'),
                        icon: 'Info',
                        onPress: () => {
                            organizationUserDetailsSideDrawer.onOpen();
                        },
                        shouldShow: () => !!singleSelectedUser,
                        testID: 'user-details',
                    },
                    {
                        title: intl('edit'),
                        onPress: editOrganizationUserSideDrawer.onOpen,
                        shouldShow: () =>
                            isOrganizationAdmin && !!singleSelectedUser && singleSelectedUser.role !== 'owner',
                        icon: 'Edit2',
                        testID: 'edit-user',
                    },
                    {
                        title: intl('deleteUser'),
                        icon: 'Trash2',
                        onPress: deleteUserButtonModal.onPress,
                        shouldShow: () =>
                            isOrganizationAdmin &&
                            selectedOrganizationUsers.length > 0 &&
                            !selectedInvitations.length &&
                            !selectedOrganizationUsers.some((s) => s.role === 'owner'),
                        shouldDisable: () => isDeletingUser || isDeletingUsers,
                        testID: 'delete-user',
                    },
                    {
                        title: intl('addInvitation'),
                        icon: 'PlusCircle',
                        onPress: () => {
                            addInvitationSideDrawer.onOpen();
                        },
                        shouldShow: (selected) => isOrganizationAdmin && selected.length === 0,
                        testID: 'add-invitation-button',
                    },
                    {
                        title: intl('resendInvitationEmail'),
                        icon: 'Send',
                        onPress: () => {
                            if (isSingleInvitationSelected) {
                                resendInvitationEmail(tableState.selectedRowKeys[0]);
                            }
                        },
                        shouldShow: () => isOrganizationAdmin && isSingleInvitationSelected,
                        testID: 'resend-invitation-email',
                        shouldDisable: () => isResendingInvitationEmail,
                    },
                    {
                        title: intl('invitationDetails'),
                        icon: 'Info',
                        onPress: () => {
                            invitationDetailsSideDrawer.onOpen();
                        },
                        shouldShow: () => !!singleSelectedInvitation && !singleSelectedUser,
                        testID: 'invitation-details',
                    },
                    {
                        title: intl('delete'),
                        icon: 'Trash2',
                        onPress: deleteInvitationButtonModal.onPress,
                        shouldShow: () =>
                            isOrganizationAdmin && selectedInvitations.length > 0 && !selectedOrganizationUsers.length,
                        shouldDisable: () => isDeletingInvitation || isDeletingInvitations,
                        testID: 'delete-invitation',
                    },
                ]}
            />
            {editOrganizationUserSideDrawer.SideDrawer}
            {organizationUserDetailsSideDrawer.SideDrawer}
            {deleteUserButtonModal.Prompt}
            {deleteInvitationButtonModal.Prompt}
            {invitationDetailsSideDrawer.SideDrawer}
            {addInvitationSideDrawer.SideDrawer}
        </>
    );
};

export default React.memo(OrganizationUsersTable);
